import { useEffect, useMemo, useState } from 'react'
import { Link, getRouteApi } from '@tanstack/react-router'
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  CircleDashed,
  FileText,
  Lock,
  PlayCircle,
  Radio,
  ListChecks,
  PartyPopper,
} from 'lucide-react'

import { LessonContent } from '@/components/lesson-content'
import { Badge } from '@/components/ui/badge'
import { ClassSessionList } from '@/components/class-sessions'
import { useCourseSessions } from '@/features/catalog/sessions'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { ProgressBar } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { lessonTypeLabel } from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import { useCourseAccess, useSaveProgress } from '@/features/learning/api'
import { formatDate, formatDateShort, formatDuration } from '@/lib/format'
import { cn } from '@/lib/cn'
import type { LessonAccess } from '@/lib/types'

const route = getRouteApi('/authenticated/app/belajar/$enrollmentId')

const typeIcons: Record<string, React.ReactNode> = {
  video: <PlayCircle className="size-4" />,
  text: <FileText className="size-4" />,
  file: <FileText className="size-4" />,
  live: <Radio className="size-4" />,
  quiz: <ListChecks className="size-4" />,
}

export function LearnPage() {
  const { enrollmentId } = route.useParams()
  const { data, isLoading } = useCourseAccess(enrollmentId)
  const saveProgress = useSaveProgress(enrollmentId)
  const toast = useToast()

  const [activeLessonId, setActiveLessonId] = useState<string | null>(null)
  const [celebrate, setCelebrate] = useState(false)
  const [curriculumOpen, setCurriculumOpen] = useState(false)

  const lessons = useMemo(
    () => data?.modules.flatMap((module) => module.lessons) ?? [],
    [data],
  )

  // Materi yang dibuka pertama kali adalah materi berikutnya menurut server,
  // sehingga pengguna langsung mendarat di tempat ia berhenti.
  useEffect(() => {
    if (activeLessonId || lessons.length === 0) return
    setActiveLessonId(data?.next_lesson_id ?? lessons[0]?.id ?? null)
  }, [activeLessonId, data?.next_lesson_id, lessons])

  if (isLoading) {
    return (
      <div className="grid gap-6 lg:grid-cols-[20rem_1fr]">
        <Skeleton className="h-96 rounded-2xl" />
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    )
  }

  if (!data) {
    return (
      <Card>
        <EmptyState
          title="Kelas tidak ditemukan"
          description="Kelas ini tidak ada di daftar belajar Anda."
          action={
            <Link to="/app/kelas-saya">
              <Button variant="outline">Kembali ke kelas saya</Button>
            </Link>
          }
        />
      </Card>
    )
  }

  const activeIndex = lessons.findIndex((lesson) => lesson.id === activeLessonId)
  const active = activeIndex >= 0 ? lessons[activeIndex] : undefined
  const previous = activeIndex > 0 ? lessons[activeIndex - 1] : undefined
  const next = activeIndex >= 0 && activeIndex < lessons.length - 1 ? lessons[activeIndex + 1] : undefined

  const markComplete = () => {
    if (!active) return

    saveProgress.mutate(
      { lessonId: active.id, seconds_watched: active.duration_minutes * 60, completed: true },
      {
        onSuccess: (result) => {
          if (result.course_completed) {
            setCelebrate(true)
            return
          }
          toast.success('Materi selesai', next ? `Lanjut ke: ${next.title}` : undefined)
          if (next?.unlocked) setActiveLessonId(next.id)
        },
        onError: () => toast.error('Gagal menyimpan kemajuan', 'Coba lagi sebentar.'),
      },
    )
  }

  const curriculum = (
    <CurriculumPanel
      modules={data.modules}
      activeLessonId={activeLessonId}
      onSelect={(lesson) => {
        setActiveLessonId(lesson.id)
        setCurriculumOpen(false)
      }}
    />
  )

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <Link
            to="/app/kelas-saya"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted transition hover:text-ink"
          >
            <ArrowLeft className="size-4" />
            Kelas saya
          </Link>
          <h1 className="mt-2 truncate text-xl font-extrabold tracking-tight text-ink sm:text-2xl">
            {data.enrollment.course.title}
          </h1>
          <p className="mt-0.5 text-sm text-muted">{data.enrollment.course.expert_name}</p>
        </div>

        <div className="w-full sm:w-64">
          <div className="flex items-center justify-between text-xs text-muted">
            <span>Kemajuan kelas</span>
            <span className="font-bold text-ink">{data.enrollment.progress_percent}%</span>
          </div>
          <ProgressBar
            value={data.enrollment.progress_percent}
            tone={data.enrollment.progress_percent === 100 ? 'success' : 'brand'}
            className="mt-1.5"
          />
        </div>
      </header>

      <Button variant="outline" className="lg:hidden" onClick={() => setCurriculumOpen(true)}>
        <ListChecks className="size-4" />
        Lihat daftar materi
      </Button>

      {/* Jadwal sesi diletakkan di atas materi, bukan di bawahnya: pertemuan
          daring terikat waktu, dan yang terikat waktu harus terlihat lebih
          dulu daripada yang dapat dibuka kapan saja. */}
      <ClassSessionsPanel slug={data.enrollment.course.slug} />

      <div className="grid gap-6 lg:grid-cols-[20rem_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">{curriculum}</div>
        </aside>

        <div>
          {active ? (
            <LessonPane
              lesson={active}
              onComplete={markComplete}
              saving={saveProgress.isPending}
              previous={previous}
              next={next}
              onNavigate={setActiveLessonId}
            />
          ) : (
            <Card>
              <EmptyState title="Pilih materi" description="Pilih salah satu materi dari daftar untuk mulai." />
            </Card>
          )}
        </div>
      </div>

      <Dialog
        open={curriculumOpen}
        onClose={() => setCurriculumOpen(false)}
        title="Daftar materi"
        description={data.enrollment.course.title}
      >
        {curriculum}
      </Dialog>

      <Dialog
        open={celebrate}
        onClose={() => setCelebrate(false)}
        title="Kelas selesai"
        description="Seluruh materi sudah Anda tuntaskan."
        footer={
          <>
            <Button variant="ghost" onClick={() => setCelebrate(false)}>
              Tutup
            </Button>
            <Link to="/kelas/$slug" params={{ slug: data.enrollment.course.slug }}>
              <Button>Tulis ulasan</Button>
            </Link>
          </>
        }
      >
        <div className="flex flex-col items-center py-6 text-center">
          <span className="flex size-16 items-center justify-center rounded-2xl bg-success-soft text-success">
            <PartyPopper className="size-8" />
          </span>
          <p className="mt-4 max-w-sm text-muted">
            Selamat! Ulasan Anda membantu peserta lain memutuskan, dan menjadi masukan berharga bagi pengajar.
          </p>
        </div>
      </Dialog>
    </div>
  )
}

function CurriculumPanel({
  modules,
  activeLessonId,
  onSelect,
}: {
  modules: Array<{ id: string; sequence: number; title: string; lessons: LessonAccess[] }>
  activeLessonId: string | null
  onSelect: (lesson: LessonAccess) => void
}) {
  return (
    <Card className="overflow-hidden">
      <nav className="thin-scroll max-h-[calc(100vh-12rem)] overflow-y-auto" aria-label="Daftar materi">
        {modules.map((module) => (
          <div key={module.id} className="border-b border-line last:border-0">
            <p className="bg-sunken px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-subtle">
              Modul {module.sequence} · {module.title}
            </p>

            <ul>
              {module.lessons.map((lesson) => {
                const active = lesson.id === activeLessonId

                return (
                  <li key={lesson.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(lesson)}
                      aria-current={active ? 'true' : undefined}
                      className={cn(
                        'flex w-full items-start gap-3 px-4 py-3 text-left transition',
                        active ? 'bg-brand-soft' : 'hover:bg-sunken',
                      )}
                    >
                      <span className="mt-0.5 shrink-0">
                        {lesson.completed ? (
                          <CheckCircle2 className="size-4 text-success" />
                        ) : !lesson.unlocked ? (
                          <Lock className="size-4 text-subtle" />
                        ) : (
                          <CircleDashed className={cn('size-4', active ? 'text-brand' : 'text-subtle')} />
                        )}
                      </span>

                      <span className="min-w-0 flex-1">
                        <span
                          className={cn(
                            'block text-sm',
                            active ? 'font-bold text-brand-ink' : 'font-medium text-ink',
                            !lesson.unlocked && 'text-muted',
                          )}
                        >
                          {lesson.title}
                        </span>
                        <span className="mt-0.5 block text-xs text-subtle">
                          {lesson.unlocked
                            ? `${lessonTypeLabel[lesson.content_type] ?? lesson.content_type}${
                                lesson.duration_minutes > 0 ? ` · ${formatDuration(lesson.duration_minutes)}` : ''
                              }`
                            : /* Materi terkunci menyebut tanggal bukanya, supaya peserta
                                 tahu kapan harus kembali alih-alih menebak. */
                              `Terbuka ${lesson.unlocks_at ? formatDateShort(lesson.unlocks_at) : 'menyusul'}`}
                        </span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </nav>
    </Card>
  )
}

function LessonPane({
  lesson,
  onComplete,
  saving,
  previous,
  next,
  onNavigate,
}: {
  lesson: LessonAccess
  onComplete: () => void
  saving: boolean
  previous?: LessonAccess
  next?: LessonAccess
  onNavigate: (lessonId: string) => void
}) {
  if (!lesson.unlocked) {
    return (
      <Card>
        <EmptyState
          icon={<Lock className="size-6" />}
          title="Materi belum terbuka"
          description={
            lesson.unlocks_at
              ? `Materi ini terbuka pada ${formatDate(lesson.unlocks_at)} sesuai jadwal kelas. Jadwal bertahap membantu Anda mencerna materi tanpa menumpuk.`
              : 'Materi ini belum terbuka sesuai jadwal kelas.'
          }
        />
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        {lesson.content_type === 'video' ? (
          <div className="flex aspect-video w-full items-center justify-center bg-inverted/90">
            {/* Pemutar sesungguhnya menyusul bersama integrasi CDN video
                bertanda tangan; kerangka ini menjaga tata letaknya tetap. */}
            <div className="text-center text-on-inverted/70">
              <PlayCircle className="mx-auto size-14" />
              <p className="mt-2 text-sm">Pemutar video akan tampil di sini</p>
            </div>
          </div>
        ) : null}

        <CardBody>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="brand" icon={typeIcons[lesson.content_type]}>
              {lessonTypeLabel[lesson.content_type] ?? lesson.content_type}
            </Badge>
            {lesson.duration_minutes > 0 ? <Badge>{formatDuration(lesson.duration_minutes)}</Badge> : null}
            {lesson.completed ? <Badge tone="success">Selesai</Badge> : null}
          </div>

          <h2 className="mt-3 text-xl font-extrabold tracking-tight text-ink">{lesson.title}</h2>

          {lesson.content ? (
            <LessonContent content={lesson.content} className="mt-4" />
          ) : (
            <p className="mt-4 text-muted">Materi ini belum memiliki catatan tertulis.</p>
          )}

          {lesson.asset_url ? (
            <a
              href={lesson.asset_url}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-brand hover:underline"
            >
              <FileText className="size-4" />
              Buka berkas pendukung
            </a>
          ) : null}
        </CardBody>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!previous}
            onClick={() => previous && onNavigate(previous.id)}
          >
            <ArrowLeft className="size-4" />
            Sebelumnya
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!next || !next.unlocked}
            onClick={() => next && onNavigate(next.id)}
          >
            Berikutnya
            <ArrowRight className="size-4" />
          </Button>
        </div>

        <Button loading={saving} disabled={lesson.completed} onClick={onComplete}>
          <CheckCircle2 className="size-4" />
          {lesson.completed ? 'Sudah selesai' : 'Tandai selesai'}
        </Button>
      </div>
    </div>
  )
}

/**
 * ClassSessionsPanel menampilkan jadwal sesi di ruang belajar.
 *
 * Panel disembunyikan sepenuhnya bila kelas ini tidak punya sesi, supaya
 * kelas mandiri tidak memuat bagian kosong yang tidak berarti apa-apa.
 */
function ClassSessionsPanel({ slug }: { slug: string }) {
  const { data: sessions } = useCourseSessions(slug)

  if (!sessions || sessions.length === 0) return null

  return (
    <section>
      <h2 className="text-lg font-bold tracking-[-0.02em] text-ink">Sesi langsung</h2>
      <ClassSessionList slug={slug} className="mt-3" />
    </section>
  )
}
